"use client"
import * as yup from 'yup';

import useUser from 'grandus-lib/hooks/useUser';
import useCart from 'grandus-lib/hooks/useCart';
import {Formik} from 'formik';
import get from 'lodash/get';
import first from 'lodash/first';
import isFunction from 'lodash/isFunction';
import TextInput from 'components/_other/form/TextInput';
import Button from 'components/_other/button/Button';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Divider from "@/components/_other/divider/Divider";
import {useTranslation} from "@/app/i18n/client";
import SocialLogin from "@/modules/user/components/auth/SocialLogin";
// import skFlag from 'public/assets/icons/sk-flag.svg';
// import czFlag from 'public/assets/icons/cz-flag.svg';
import Image from 'next/image';

const LoginTitle = ({ title, className = '' }) => {
  // const getLocaleFlag = locale => {
  //   switch (locale) {
  //     case 'sk':
  //       return skFlag;
  //     case 'cz':
  //       return czFlag;
  //     default:
  //       return null;
  //   }
  // };

  const flag = null;//getLocaleFlag(process?.env?.NEXT_PUBLIC_DEFAULT_LOCALE);

  return (
    <h1 className={className}>
      {title}{' '}
      {flag ? (
        <span>
          {' '}
          <Image
            className={'inline'}
            src={flag}
            alt={`${process?.env?.NEXT_PUBLIC_DEFAULT_LOCALE} vlajka`}
            width={30}
            height={30}
          />
        </span>
      ) : (
        ''
      )}
    </h1>
  );
};

const LoginForm = ({
  afterLoginCallback = null,
  email = '',
  layout = 'grid',
  title,
  redirectToProfile,
}) => {
  const { t } = useTranslation();
  const { mutateUser } = useUser();
  const { cart, mutateCart } = useCart();
  const router = useRouter();
  const formProps = {
    enableReinitialize: true,
    initialValues: {
      email: email || '',
      password: '',
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .nullable()
        .email(t('login.email.email_validation'))
        .required(t('login.email.required_validation')),
      password: yup.string().required(t('login.password.required_validation')),
    }),
    onSubmit: async (values, { setFieldError, setErrors }) => {
      const { email, password } = values;
      const reqBody = { user: { email: email, password: password } };
      if (cart) {
        reqBody.cart = { accessToken: cart?.accessToken };
      }
      try {
        const user = await fetch(`/api/lib/v1/auth/signin`, {
          method: 'POST',
          body: JSON.stringify(reqBody),
        }).then(response => response.json());
        const message = get(first(user), 'message');
        if (!message) {
          await mutateUser({ ...user });
          if (user?.cart) {
            await mutateCart(get(user, 'cart'), false);
          }

          if (isFunction(afterLoginCallback)) {
            await afterLoginCallback(user);
          }

          if (redirectToProfile) {
            router.push('/profil');
          }
        } else {
          setFieldError('email', message);
          setFieldError('password', message);
        }
      } catch (error) {
        console.error('An unexpected error happened:', error);
        setErrors(error.data.message);
      }

      return false;
    },
  };

  return (
    <Formik {...formProps}>
      {({
        values,
        errors,
        touched,
        isSubmitting,
        isValid,
        handleChange,
        handleBlur,
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className={'flex flex-col h-full'}>
          <div className={'grid grid-cols-1 gap-4'}>
            {title ? (
              <LoginTitle className="col-span-full" title={title} />
            ) : (
              ''
            )}
            <div className={'col-span-2 md:col-span-1'}>
              <TextInput
                required
                label={t('login.email.label')}
                error={touched.email && errors.email ? errors.email : ''}
                inputProps={{
                  id: 'email',
                  name: 'email',
                  onChange: handleChange,
                  onBlur: handleBlur,
                  value: values?.email,
                  autoComplete: 'email',
                }}
              />
            </div>
            <div className={'col-span-2 md:col-span-1'}>
              <TextInput
                required
                label={t('login.password.label')}
                error={
                  touched.password && errors.password ? errors.password : ''
                }
                inputProps={{
                  id: 'password',
                  name: 'password',
                  type: 'password',
                  onChange: handleChange,
                  onBlur: handleBlur,
                  value: values?.password,
                  autoComplete: 'current-password',
                }}
              />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="w-full">
              <Divider />
            </div>
            <Button
              htmlType="submit"
              loading={isValid && isSubmitting}
              disabled={!isValid}
            >
              <span>{t('login.login')}</span>
            </Button>
          </div>
          <SocialLogin className={'min-w-full'} />

          <div className={'mt-6 mb-6 text-center'}>
            <Link href={'/zabudnute-heslo'} className={'text-inherit'}>
              {t('login.forgotten_password')}
            </Link>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default LoginForm;
